import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import { ChevronDownIcon } from '#/shared/ui/icons'
import type { SelectOption } from '#/shared/ui/select'
import { TemplatePicker } from '#/shared/ui/template-picker'

import type {
  InsertFieldGroup,
  InsertFieldOption,
} from '../lib/use-template-variable-options'

const PILL_CLASS =
  'group flex h-8 cursor-pointer items-center gap-1.25 rounded-full border border-gray-200 bg-gray-50 px-3.75 text-sm text-gray-900 outline-none transition-colors hover:bg-gray-100 focus-visible:focus-ring data-[state=open]:focus-ring'

const PillTrigger = forwardRef<
  HTMLButtonElement,
  { label: string } & React.ComponentPropsWithoutRef<'button'>
>(({ label, ...props }, ref) => (
  <button ref={ref} type="button" className={PILL_CLASS} {...props}>
    <span className="select-none">{label}</span>
    <ChevronDownIcon className="size-2.5 text-gray-500 transition-transform duration-200 group-data-[state=open]:rotate-180" />
  </button>
))
PillTrigger.displayName = 'PillTrigger'

function InsertFieldOptionRow({
  option,
  onSelect,
}: {
  option: InsertFieldOption
  onSelect: (option: InsertFieldOption) => void
}) {
  return (
    <DropdownMenuItem onSelect={() => onSelect(option)}>
      <span>{option.label}</span>
      {option.suffix && (
        <DropdownMenuShortcut>{option.suffix}</DropdownMenuShortcut>
      )}
    </DropdownMenuItem>
  )
}

function InsertFieldGroupContent({
  group,
  onSelect,
}: {
  group: InsertFieldGroup
  onSelect: (option: InsertFieldOption) => void
}) {
  return (
    <>
      {group.items.map((item) => (
        <InsertFieldOptionRow key={item.id} option={item} onSelect={onSelect} />
      ))}
      {group.subgroups.map((subgroup) => (
        <DropdownMenuSub key={subgroup.label}>
          <DropdownMenuSubTrigger>{subgroup.label}</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <InsertFieldGroupContent group={subgroup} onSelect={onSelect} />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      ))}
    </>
  )
}

function InsertFieldPill({
  groups,
  onSelect,
}: {
  groups: InsertFieldGroup[]
  onSelect: (option: InsertFieldOption) => void
}) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <PillTrigger label={t('templateToolbar.insertField')} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {groups.length === 0 ? (
          <DropdownMenuItem disabled>
            {t('templateToolbar.noFields')}
          </DropdownMenuItem>
        ) : (
          groups.map((group) => (
            <DropdownMenuSub key={group.label}>
              <DropdownMenuSubTrigger>{group.label}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <InsertFieldGroupContent group={group} onSelect={onSelect} />
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface TemplateComposeToolbarProps {
  templateOptions: SelectOption[]
  templateLoading?: boolean
  onTemplateSelect: (value: string) => void
  // Omit both to hide the insert-field dropdown (e.g. when the editor's `/`
  // menu provides field insertion instead).
  insertFieldGroups?: InsertFieldGroup[]
  onInsertFieldOption?: (option: InsertFieldOption) => void
}

export function TemplateComposeToolbar({
  templateOptions,
  templateLoading,
  onTemplateSelect,
  insertFieldGroups,
  onInsertFieldOption,
}: TemplateComposeToolbarProps) {
  return (
    <div className="flex shrink-0 items-center gap-2.5 border-b border-gray-200 px-2.5 py-2.5">
      <div className="min-w-0 max-w-48 flex-1">
        <TemplatePicker
          options={templateOptions}
          loading={templateLoading}
          onSelect={onTemplateSelect}
        />
      </div>
      {insertFieldGroups && onInsertFieldOption && (
        <InsertFieldPill
          groups={insertFieldGroups}
          onSelect={onInsertFieldOption}
        />
      )}
    </div>
  )
}
